import { useState, type FC, type FormEvent } from "react";
import { addNotification } from "../../stores/notification_store";
import {
  NOTIFICATION_TRIGGERS,
  HOOK_TYPES,
  type NotificationTrigger,
  type NotificationHookType,
  type NotificationHook,
} from "../../../../shared/models/notification";

const TRIGGER_BADGE: Record<
  NotificationTrigger,
  { cls: string; label: string }
> = {
  issued: { cls: "vmx-badge-success", label: "Issued" },
  expiring: { cls: "vmx-badge-warning", label: "Expiring" },
  expired: { cls: "vmx-badge-danger", label: "Expired" },
  revoked: { cls: "vmx-badge-neutral", label: "Revoked" },
  renewed: { cls: "vmx-badge-accent", label: "Renewed" },
};

export const CreateNotificationModal: FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState<NotificationTrigger>("expiring");
  const [hooks, setHooks] = useState<NotificationHook[]>([
    { type: "webhook", endpoint: "", enabled: true },
  ]);

  const addHook = () => {
    setHooks((prev) => [
      ...prev,
      { type: "webhook", endpoint: "", enabled: true },
    ]);
  };

  const removeHook = (idx: number) => {
    setHooks((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateHookType = (idx: number, type: NotificationHookType) => {
    setHooks((prev) => prev.map((h, i) => (i === idx ? { ...h, type } : h)));
  };

  const updateHookEndpoint = (idx: number, endpoint: string) => {
    setHooks((prev) =>
      prev.map((h, i) => (i === idx ? { ...h, endpoint } : h))
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName === "") return;
    const validHooks = hooks.filter((h) => h.endpoint.trim() !== "");
    if (validHooks.length === 0) return;
    addNotification({
      name: trimmedName,
      trigger,
      hooks: validHooks,
    });
    onClose();
  };

  const placeholders: Partial<Record<NotificationHookType, string>> = {
    webhook: "https://hooks.example.com/notify",
    amazon_sns: "arn:aws:sns:us-east-1:123456789:topic",
    amazon_sqs: "https://sqs.us-east-1.amazonaws.com/123456789/queue",
    slack: "https://hooks.slack.com/services/T00/B00/xxx",
    pagerduty: "https://events.pagerduty.com/integration/xxx/enqueue",
    email: "alerts@example.com",
    microsoft_teams: "https://outlook.office.com/webhook/xxx",
    google_pubsub: "projects/my-project/topics/my-topic",
  };

  return (
    <div className="vmx-modal" style={{ maxWidth: 600 }}>
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">New Notification</div>
        <button
          className="vmx-btn vmx-btn-ghost vmx-btn-icon"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="vmx-modal-body">
          <div className="vmx-form-group">
            <label className="vmx-label">Notification Name</label>
            <input
              className="vmx-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Expiring Certificate Alert"
              required
              autoFocus
            />
          </div>

          <div className="vmx-form-group">
            <label className="vmx-label">Trigger Event</label>
            <select
              className="vmx-select"
              value={trigger}
              onChange={(e) =>
                setTrigger(e.target.value as NotificationTrigger)
              }
            >
              {NOTIFICATION_TRIGGERS.map((t) => (
                <option key={t} value={t}>
                  {TRIGGER_BADGE[t].label} - When a certificate is {t}
                </option>
              ))}
            </select>
          </div>

          <div className="vmx-form-group">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <label className="vmx-label">Hooks</label>
              <button
                type="button"
                className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                onClick={addHook}
              >
                ＋ Add Hook
              </button>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginTop: 8,
              }}
            >
              {hooks.map((hook, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                  }}
                >
                  <select
                    className="vmx-select"
                    value={hook.type}
                    onChange={(e) =>
                      updateHookType(
                        idx,
                        e.target.value as NotificationHookType
                      )
                    }
                    style={{ maxWidth: 180, flexShrink: 0 }}
                  >
                    {HOOK_TYPES.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </select>
                  <input
                    className="vmx-input"
                    value={hook.endpoint}
                    onChange={(e) => updateHookEndpoint(idx, e.target.value)}
                    placeholder={placeholders[hook.type] ?? "Endpoint"}
                    required
                    style={{ flex: 1 }}
                  />
                  {hooks.length > 1 && (
                    <button
                      type="button"
                      className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                      style={{ color: "var(--vmx-danger)", flexShrink: 0 }}
                      onClick={() => removeHook(idx)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="vmx-modal-footer">
          <button
            type="button"
            className="vmx-btn vmx-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button type="submit" className="vmx-btn vmx-btn-primary">
            Create Notification
          </button>
        </div>
      </form>
    </div>
  );
};
